import { getCloudFunctionUrl } from "../firebase";

const FUNCTION_URL = getCloudFunctionUrl("declineStudentApplication");
const PENDING_KEY = "enrollease.pendingStudentDeclines.v1";

function readPending() {
  const serialized = window.localStorage.getItem(PENDING_KEY);
  if (!serialized) return {};
  const pending = JSON.parse(serialized);
  if (!pending || typeof pending !== "object" || Array.isArray(pending)) {
    throw new Error("Stored application-decline recovery information is invalid.");
  }
  return pending;
}

function writePending(pending) {
  window.localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
}

function requestFingerprint({ studentDocumentId, reason, details }) {
  return JSON.stringify([studentDocumentId, reason, details.trim()]);
}

function getOperation({ studentDocumentId, reason, details }) {
  const pending = readPending();
  const fingerprint = requestFingerprint({ studentDocumentId, reason, details });
  const existing = pending[studentDocumentId];
  if (existing) {
    if (existing.fingerprint !== fingerprint) {
      throw new Error("This application has a pending decline recovery. Retry it before changing the decline reason or explanation.");
    }
    return { pending, operationId: existing.operationId };
  }
  const operationId = window.crypto?.randomUUID?.();
  if (!operationId) throw new Error("Secure decline request IDs are unavailable.");
  pending[studentDocumentId] = { operationId, fingerprint };
  writePending(pending);
  return { pending, operationId };
}

function removePending(studentDocumentId, operationId) {
  const pending = readPending();
  if (pending[studentDocumentId]?.operationId === operationId) {
    delete pending[studentDocumentId];
    writePending(pending);
  }
}

async function post(user, body) {
  if (!user) throw new Error("Sign in again before processing this application.");
  const token = await user.getIdToken();
  return fetch(FUNCTION_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });
}

export async function previewStudentApplicationDecline({
  user,
  studentDocumentId,
  reason,
  details
}) {
  const response = await post(user, {
    action: "preview",
    studentDocumentId,
    reason,
    details
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || typeof result?.subject !== "string" || typeof result?.text !== "string") {
    throw new Error(result?.error?.message || "The decline email preview could not be prepared.");
  }
  return result;
}

export async function declineStudentApplication({
  user,
  studentDocumentId,
  reason,
  details = ""
}) {
  const { operationId } = getOperation({ studentDocumentId, reason, details });
  let response;
  try {
    response = await post(user, {
      operationId,
      studentDocumentId,
      reason,
      details
    });
  } catch (error) {
    throw error;
  }
  const result = await response.json().catch(() => null);
  if (response.status === 202) {
    if (result?.outcome !== "recovery_pending" || result.operationId !== operationId) {
      throw new Error("The server returned an invalid decline-recovery response.");
    }
    return result;
  }
  if (!response.ok || result?.ok !== true || result?.outcome !== "declined") {
    if (
      response.status < 500 &&
      !["operation_conflict", "decline_in_progress", "email_delivery_uncertain"].includes(result?.error?.code)
    ) {
      removePending(studentDocumentId, operationId);
    }
    const error = new Error(result?.error?.message || "The application could not be declined.");
    error.code = result?.error?.code || "decline_failed";
    throw error;
  }
  removePending(studentDocumentId, operationId);
  return result;
}

export async function recoverStudentApplicationDecline({
  user,
  operationId,
  studentDocumentId,
  resolveEmailDelivery
}) {
  const response = await post(user, {
    operationId,
    ...(resolveEmailDelivery ? { resolveEmailDelivery } : {})
  });
  const result = await response.json().catch(() => null);
  if (response.status === 202) {
    if (result?.outcome !== "recovery_pending" || result.operationId !== operationId) {
      throw new Error("The server returned an invalid decline-recovery response.");
    }
    return result;
  }
  if (!response.ok || result?.ok !== true || result?.outcome !== "declined") {
    const error = new Error(result?.error?.message || "The application decline recovery failed.");
    error.code = result?.error?.code || "decline_recovery_failed";
    throw error;
  }
  removePending(studentDocumentId, operationId);
  return result;
}

export function getPendingStudentDeclines() {
  return Object.entries(readPending()).map(([studentDocumentId, value]) => ({
    studentDocumentId,
    operationId: value.operationId
  }));
}
